import * as React from 'react';
import { Check } from 'lucide-react';
import { cn } from '@/lib/utils/cn';
import type { StepState } from './steps';

/**
 * A guided start (prototype `.steps` / `.guide-body`, shared by the
 * discovery and vendor knowledge base prototypes): numbered steps on a
 * line, each ticked when its work is done, the one to do now in orange
 * with its action. Each step's state comes from the caller, from server
 * data; the guide never decides what is done.
 *
 *   <StepGuide aria-label="Where to start" steps={[{ key, title, state, body, note, action }]} />
 */

export interface GuideStep {
  key: string;
  title: string;
  state: StepState;
  /** What the step does, or what it found. */
  body: React.ReactNode;
  /** A short note under the body while the step is not done. */
  note?: React.ReactNode;
  /** Shown on the current step only: its form or its button. */
  action?: React.ReactNode;
}

export function StepGuide({
  steps,
  'aria-label': ariaLabel,
  className,
}: {
  steps: GuideStep[];
  'aria-label': string;
  className?: string;
}) {
  return (
    <ol
      aria-label={ariaLabel}
      className={cn(
        'relative m-0 list-none p-0 before:absolute before:bottom-3.5 before:left-[13px] before:top-3.5 before:w-0.5 before:bg-ink-100',
        className,
      )}
    >
      {steps.map((step, i) => (
        <li
          key={step.key}
          data-state={step.state}
          aria-current={step.state === 'current' ? 'step' : undefined}
          className="relative mb-3"
        >
          <div className="flex items-center gap-3">
            <span
              aria-hidden="true"
              className={cn(
                'relative z-[1] inline-flex h-7 w-7 flex-none items-center justify-center rounded-full border-2 text-meta font-semibold',
                step.state === 'done' && 'border-posture-healthy bg-posture-healthy text-paper-0',
                step.state === 'current' &&
                  'border-accent-amber-600 bg-accent-amber-600 text-paper-0',
                step.state === 'todo' && 'border-ink-200 bg-paper-0 text-ink-500',
              )}
            >
              {step.state === 'done' ? <Check className="h-[13px] w-[13px]" /> : i + 1}
            </span>
            <span
              className={cn(
                'text-h3 font-semibold',
                step.state === 'todo' ? 'text-ink-500' : 'text-ink-900',
              )}
            >
              {step.title}
              <span className="sr-only">
                {step.state === 'done'
                  ? ' (done)'
                  : step.state === 'current'
                    ? ' (do this now)'
                    : ''}
              </span>
            </span>
          </div>
          <div
            className={cn(
              'ml-10 mt-1 pb-1.5',
              step.state === 'current' &&
                'mt-2 rounded-card border border-l-4 border-accent-amber-100 border-l-accent-amber-600 bg-paper-0 px-4 py-3.5',
            )}
          >
            <div
              className={cn(
                'text-body-sm',
                step.state === 'todo' ? 'text-ink-500' : 'text-ink-700',
              )}
            >
              {step.body}
            </div>
            {step.note && step.state !== 'done' ? (
              <div className="mt-1.5 flex gap-1.5 text-meta text-ink-500">{step.note}</div>
            ) : null}
            {step.state === 'current' && step.action ? (
              <div className="mt-2.5">{step.action}</div>
            ) : null}
          </div>
        </li>
      ))}
    </ol>
  );
}

/** The states of steps in order: done where done, the first not done is current. */
export function stepStates(done: boolean[]): StepState[] {
  const current = done.findIndex((d) => !d);
  return done.map((d, i) => (d ? 'done' : i === current ? 'current' : 'todo'));
}
